import React, { useContext, useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import { useLocation, useParams } from 'react-router-dom';
import { useHistory } from 'react-router';

import BasicPageLayout from '../../components/layouts/BasicPageLayout';
import { User } from '../../core/graphql/types';
import { fetchUserById } from '../../core/services/userSvc';
import { authStore } from '../../core/stores/auth';
import { AntTab, AntTabs } from '../../components/ui/AntTabs';
import AthleteChannelBasics, { AthleteChannelBasicsUpdate } from '../../containers/AthleteChannelBasics';
import { appStore } from '../../core/stores/app';
import SpinnerBox from '../../components/ui/SpinnerBox';
import AthleteChannelSocial, { AthleteChannelSocialUpdate } from '../../containers/AthleteChannelSocial';
import AthletePosts from '../../containers/AthletePosts';
import AthleteChannelFunding, { AthleteChannelFundingUpdate } from '../../containers/AthleteChannelFunding';

const defaultSectionId = 'basics';
const sections = [
  { id: 'basics', title: 'Your channel' },
  { id: 'social', title: 'Social accounts' },
  { id: 'funding', title: 'Funding' },
  { id: 'posts', title: 'Posts' },
];
const notFoundErrorMsg = 'It seems you are trying to access a private page';

const AthleteChannelPage: React.FC = () => {
  const { sectionId = defaultSectionId }: { sectionId: string } = useParams();
  const location = useLocation();
  const history = useHistory();
  const [authState] = useContext(authStore);
  const [appState] = useContext(appStore);
  const [selectedTab, setSelectedTab] = useState(0);
  const [user, setUser] = useState<User | undefined>();

  const loadUser = async (userId: string) => {
    const user = await fetchUserById(userId, 'full');

    if (user) {
      setUser(user);
    } else {
      history.push({
        pathname: '/notfound',
        state: {
          message: notFoundErrorMsg,
        },
      });
    }
  };
  const handleSelectedTabChange = (event: React.ChangeEvent<any>, newValue: number) => {
    if (newValue < sections.length) {
      const nextSection = sections[newValue].id || defaultSectionId;
      const pathRoot = location.pathname.split('/')[1];

      history.push({
        pathname: `/${pathRoot}/${nextSection}`,
      });
    }
  };
  const handleUserUpdated = (
    updateObj: AthleteChannelBasicsUpdate | AthleteChannelSocialUpdate | AthleteChannelFundingUpdate,
  ) => {
    setUser((user) => {
      if (user) {
        return { ...user, ...updateObj };
      }
    });
  };

  useEffect(() => {
    const sectionIndex = sections.findIndex((s) => s.id === sectionId);

    setSelectedTab(sectionIndex >= 0 ? sectionIndex : sections.findIndex((s) => s.id === defaultSectionId));
  }, [sectionId]);

  useEffect(() => {
    try {
      (async function () {
        if (authState.user) {
          loadUser(authState.user.userId);
        }
      })();
    } catch (e) {
      history.push({
        pathname: '/notfound',
        state: {
          message: notFoundErrorMsg,
        },
      });
    }
  }, [authState.user]);

  return (
    <BasicPageLayout
      title="Channel"
      maxWidth={appState.sideMenuVisible ? 'lg' : 'md'}
      showTitle={!appState.sideMenuVisible}
    >
      {/* Tab menu */}
      <Box mb={2}>
        <AntTabs
          value={selectedTab}
          onChange={handleSelectedTabChange}
          aria-label="ant example"
          variant="scrollable"
          scrollButtons="auto"
        >
          {sections.map((s, i) => (
            <AntTab key={i} label={s.title} />
          ))}
        </AntTabs>
      </Box>

      {/* Content */}
      <Box>
        {!user && <SpinnerBox />}
        {user && (
          <>
            {/* Channel Basics */}
            {sections[selectedTab].id === 'basics' && (
              <AthleteChannelBasics user={user} onUserUpdated={handleUserUpdated} />
            )}

            {/* Channel social */}
            {sections[selectedTab].id === 'social' && (
              <AthleteChannelSocial user={user} onUserUpdated={handleUserUpdated} />
            )}

            {/* Funding */}
            {sections[selectedTab].id === 'funding' && (
              <AthleteChannelFunding user={user} onUserUpdated={handleUserUpdated} />
            )}

            {/* Channel posts */}
            {sections[selectedTab].id === 'posts' && (
              <AthletePosts athlete={user} editable={true} isUserSubscribed={true} />
            )}
          </>
        )}
      </Box>
    </BasicPageLayout>
  );
};

export default AthleteChannelPage;
